import {DEFAULT_RGB} from "../../constants/config";
import {getColorIntensity} from "../../helpers/colorHelper";

/**
 * @class Color
 */
export default class Color {
    /**
     * @param {Number}  id
     * @param {String}  name
     * @param {String}  displayName
     * @param {String}  code
     * @param {String}  rgb
     * @param {Boolean} local
     * @param {Boolean} hasFormula
     */
    constructor(id, name, displayName, code, rgb = DEFAULT_RGB, local = false, hasFormula = true) {
        this._id = id;
        this._name = name;
        this._displayName = displayName;
        this._code = code;
        this._rgb = rgb;
        this._local = local;
        this._hasFormula = hasFormula;
        this._intensity = getColorIntensity(rgb);

        if (typeof this._id !== "number") {
            throw new Error("Invalid datatype of ID - Color");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {String}
     */
    get name() {
        return this._name;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._name = name;
    }

    /**
     * @return {String}
     */
    get displayName() {
        return this._displayName;
    }

    /**
     * @param {String} displayName
     */
    set displayName(displayName) {
        this._displayName = displayName;
    }

    /**
     * @return {String}
     */
    get code() {
        return this._code;
    }

    /**
     * @param {String} code
     */
    set code(code) {
        this._code = code;
    }

    /**
     * @return {String}
     */
    get rgb() {
        return this._rgb;
    }

    /**
     * @param {String} rgb
     */
    set rgb(rgb) {
        this._rgb = rgb;
        this._intensity = getColorIntensity(rgb);
    }

    /**
     * @return {Boolean}
     */
    get local() {
        return this._local;
    }

    /**
     * @param {Boolean} local
     */
    set local(local) {
        this._local = local;
    }

    /**
     * @return {Boolean}
     */
    get hasFormula() {
        return this._hasFormula;
    }

    /**
     * @param {Boolean} hasFormula
     */
    set hasFormula(hasFormula) {
        this._hasFormula = hasFormula;
    }

    /**
     * @return {Number}
     */
    get intensity() {
        return this._intensity;
    }

    /**
     * Compatibility purpose.
     *
     * @return {Number | null}
     */
    get fandeckId() {
        return null;
    }

    /**
     * @return {String}
     */
    toString() {
        return "Color (" + this._id + ")";
    }

    /**
     * @return {Color}
     */
    clone = () => {
        return new Color(this._id, this._name, this._displayName, this._code, this._rgb, this._local, this._hasFormula);
    };

    /**
     * @param   {Color}         color
     * @return  {String | null}
     */
    static serialize = (color) => {
        try {
            return (
                color.id +
                ";" +
                (color.name === null ? "null" : color.name.replace(/;/g, "")) +
                ";" +
                (color.displayName === null ? "null" : color.displayName.replace(/;/g, "")) +
                ";" +
                (color.code === null ? "null" : color.code.replace(/;/g, "")) +
                ";" +
                (color.rgb === null ? "null" : color.rgb) +
                ";" +
                color.local +
                ";" +
                color.hasFormula
            );
        } catch (e) {
            return null;
        }
    };

    /**
     * @param   {String | null} color
     * @return  {Color | null}
     */
    static unserialize = (color) => {
        if (typeof color !== "string") {
            return null;
        }

        let colorParts = color.split(";");

        if (colorParts.length !== 7) {
            return null;
        }

        try {
            return new Color(
                Number(colorParts[0]),
                colorParts[1] === "null" ? null : colorParts[1],
                colorParts[2] === "null" ? null : colorParts[2],
                colorParts[3] === "null" ? null : colorParts[3],
                colorParts[4] === "null" ? null : colorParts[4],
                colorParts[5] === "true",
                colorParts[6] === "true"
            );
        } catch (e) {
            return null;
        }
    };

    /**
     * Clones color object and updates name property.
     *
     * @param   {String} name
     * @return  {Object}
     */
    updateName = (name) => {
        let newColor = this.clone();
        newColor.name = name;

        return newColor;
    };

    /**
     * Clones color object and updates code property.
     *
     * @param   {String} code
     * @return  {Object}
     */
    updateCode = (code) => {
        let newColor = this.clone();
        newColor.code = code;

        return newColor;
    };

    /**
     * Clones color object and updates rgb property.
     *
     * @param   {String} rgb
     * @return  {Object}
     */
    updateRgb = (rgb) => {
        let newColor = this.clone();
        newColor.rgb = rgb;

        return newColor;
    };

    /**
     * @param   {Boolean}   blendFormulaAndCode
     * @param   {Boolean}   formulaCodePrimary
     * @return  {String}
     */
    updateLongDisplayName = (blendFormulaAndCode, formulaCodePrimary) => {
        if (!blendFormulaAndCode || this._code === null || this._displayName === this._code) {
            return this;
        }

        let newColor = this.clone();

        if (formulaCodePrimary) {
            newColor.displayName = this._code + "   " + this._displayName;

            return newColor;
        }

        newColor.displayName = this._displayName + "   " + this._code;

        return newColor;
    };

    /**
     * @param   {Boolean}   formulaCodePrimary
     * @return  {String}
     */
    getLongDisplayName = (formulaCodePrimary) => {
        if (this._code === null || this._displayName === this._code) {
            return this._displayName;
        }

        if (formulaCodePrimary) {
            return this._code + "   " + this._displayName;
        }

        return this._displayName + "   " + this._code;
    };

    /**
     * Clones color object and updates code and rgb property.
     *
     * @param   {String} code
     * @param   {String} rgb
     * @return  {Object}
     */
    updateCodeAndRgb = (code, rgb) => {
        let newColor = this.clone();
        newColor.rgb = rgb;

        if (code !== null) {
            newColor.code = code;
        }

        return newColor;
    };

    /**
     * @return {String}
     */
    getKey() {
        if (this._local) {
            return "local-" + this._id;
        }

        return "default-" + this._id;
    }

    /**
     * @return {String}
     */
    getUniqueKey() {
        return this.getKey() + "-" + this._rgb;
    }

    /**
     * @return {Object}
     */
    formatedObject() {
        return {
            id: this._id,
            name: this._name,
            displayName: this._displayName,
            code: this._code,
            rgb: this._rgb,
            local: this._local,
            hasFormula: this._hasFormula,
            intensity: this._intensity
        };
    }
}
